import { QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { ApiClientProvider } from '@platform/api-client/react';

import { AppErrorBoundary, RouteErrorFallback } from './components/error-boundary';
import { apiClient, queryClient } from './lib/api/client';
import { PlatformClientProvider, platformClient } from './lib/api/platform-client-context';
import { initSentry } from './lib/sentry';
import { routeTree } from './routeTree.gen';
import './styles.css';

/**
 * Two clients, on purpose (M2.8, M12.7).
 *
 * - **`apiClient`** is the typed transport from `@platform/api-client`. Authentication,
 *   the session summary and impersonation go through it directly.
 * - **`platformClient`** is the `PlatformClient` seam over the same transport: every
 *   endpoint in `Docs/API.md` §2.13, with the `{ data }` envelope unwrapped. Which
 *   implementation it is - the API or the in-memory mock - is decided once in
 *   `lib/api/platform-client-context.tsx` from `VITE_USE_MOCKS` and the build mode.
 *
 * They are separate because they answer different questions: one is "who is signed in",
 * which every guard reads, and the other is "what is on the platform", which the screens
 * read through a seam that can be driven without a server.
 *
 * The query client and its cache live in `lib/api/client.ts` alongside the API client,
 * because a `401` from any endpoint has to put the session cache back to anonymous.
 * **No persister is installed and none may be**: this console reads across tenants, so
 * anything in `localStorage` or IndexedDB would leave another clinic's data in an
 * operator's browser (ADR-007, SECURITY.md §7).
 */

/* `defaultErrorComponent` is the route-level half of SR-54: a screen that throws
   loses itself and keeps the shell, instead of taking the whole app down with it.
   The fallback never renders `error.message` - see `components/error-boundary`. */
const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultErrorComponent: RouteErrorFallback,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Before the first render, so an error thrown while mounting is still reported
// (M14.5). A no-op unless VITE_SENTRY_DSN is set; see lib/sentry.ts.
initSentry();

const container = document.getElementById('root');
if (container === null) {
  throw new Error('#root is missing from index.html');
}

createRoot(container).render(
  <StrictMode>
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ApiClientProvider client={apiClient}>
          <PlatformClientProvider client={platformClient}>
            <RouterProvider router={router} />
          </PlatformClientProvider>
        </ApiClientProvider>
      </QueryClientProvider>
    </AppErrorBoundary>
  </StrictMode>,
);
